import JSZip from "jszip";

import type { DocDetail } from "@xop/dashboard-shared";

import { fetchDocFileBytes, fetchDocSourceFileBytes, getDoc } from "./api.js";
import { buildDocPdf } from "./docPdf.js";

/**
 * What "Download" means for ONE document — the single rule shared by the
 * reader's Download button and the folder page's bulk ZIP (2026-09-23):
 *   • the faithful original PDF (`source_file`) when there is one;
 *   • else the uploaded file, whatever its type;
 *   • else (a hand-written doc) a PDF built from its body.
 * Bytes come through the authenticated same-origin passthrough, so this
 * behaves the same on the web and in the desktop shell.
 */
export interface DocDownloadDeps {
  fetchSource: typeof fetchDocSourceFileBytes;
  fetchFile: typeof fetchDocFileBytes;
  buildPdf: typeof buildDocPdf;
}

const realDeps: DocDownloadDeps = {
  fetchSource: fetchDocSourceFileBytes,
  fetchFile: fetchDocFileBytes,
  buildPdf: buildDocPdf,
};

export async function resolveDocDownload(
  d: DocDetail,
  deps: DocDownloadDeps = realDeps,
): Promise<{ filename: string; blob: Blob }> {
  const sourceFile = d.source_file ?? null;
  const file = d.files[0];
  if (sourceFile) {
    const { bytes, contentType } = await deps.fetchSource(d.id);
    return {
      filename: sourceFile.filename || `${d.slug}.pdf`,
      blob: new Blob([bytes], { type: contentType ?? "application/pdf" }),
    };
  }
  if (file) {
    const { bytes, contentType } = await deps.fetchFile(d.id);
    return {
      filename: file.filename || d.slug,
      blob: new Blob([bytes], { type: contentType ?? file.content_type ?? "application/octet-stream" }),
    };
  }
  return deps.buildPdf(d, { fetchFileBytes: deps.fetchFile });
}

/** Keep every name in a ZIP unique: "a.pdf", "a (2).pdf", "a (3).pdf". */
export function uniqueName(name: string, taken: Set<string>): string {
  const safe = name.replace(/[\\/]+/g, "-").trim() || "document";
  if (!taken.has(safe.toLowerCase())) {
    taken.add(safe.toLowerCase());
    return safe;
  }
  const dot = safe.lastIndexOf(".");
  const base = dot > 0 ? safe.slice(0, dot) : safe;
  const ext = dot > 0 ? safe.slice(dot) : "";
  for (let n = 2; ; n++) {
    const candidate = `${base} (${n})${ext}`;
    if (!taken.has(candidate.toLowerCase())) {
      taken.add(candidate.toLowerCase());
      return candidate;
    }
  }
}

/**
 * Download several documents: ONE document saves as its own file, several
 * go into `<zipName>.zip`. Documents are fetched one at a time (the proxy is
 * shared with everyone else's requests); a document that fails is skipped and
 * named in `failed`, so one broken file never loses the rest.
 */
export async function buildBulkDownload(
  ids: ReadonlyArray<number>,
  zipName: string,
  opts: {
    load?: (id: number) => Promise<DocDetail>;
    deps?: DocDownloadDeps;
    onProgress?: (done: number, total: number) => void;
  } = {},
): Promise<{ filename: string; blob: Blob; failed: string[] } | null> {
  const load = opts.load ?? (async (id: number) => (await getDoc(id)).data);
  const files: Array<{ filename: string; blob: Blob }> = [];
  const failed: string[] = [];
  let done = 0;
  for (const id of ids) {
    let title = `#${id}`;
    try {
      const d = await load(id);
      title = d.title || d.slug;
      files.push(await resolveDocDownload(d, opts.deps));
    } catch {
      failed.push(title);
    }
    done += 1;
    opts.onProgress?.(done, ids.length);
  }
  if (files.length === 0) return null;
  if (files.length === 1 && ids.length === 1) return { ...files[0]!, failed };

  const zip = new JSZip();
  const taken = new Set<string>();
  for (const f of files) zip.file(uniqueName(f.filename, taken), f.blob);
  const blob = await zip.generateAsync({ type: "blob" });
  const base = zipName.replace(/[\\/:*?"<>|]+/g, "-").trim() || "documents";
  return { filename: `${base}.zip`, blob, failed };
}
